/**
 * Formatting helpers.
 *
 * EVERY date component shown in this panel is rendered in **Asia/Tashkent**
 * (§2) — never the browser's zone. A handler in another timezone reading
 * "due tomorrow" off a UTC-rendered deadline would miss a statutory date.
 */
import { TIMEZONE } from '@navoiy/domain';

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DATE_ONLY = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  const value = new Date(iso);
  return Number.isNaN(value.getTime()) ? '—' : DATE_TIME.format(value).replace(',', '');
}

export function formatDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  const value = new Date(iso);
  return Number.isNaN(value.getTime()) ? '—' : DATE_ONLY.format(value);
}

/** `<input type="datetime-local">` needs a value with no zone suffix. */
export function toDateInput(iso: string | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

/** A date-only filter bound sent to the API as an RFC 3339 instant. */
export function fromDateInput(value: string, endOfDay = false): string | undefined {
  if (!value) return undefined;
  return `${value}T${endOfDay ? '23:59:59' : '00:00:00'}+05:00`;
}

const CLOCK = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/**
 * The top bar's wall clock (§14.7).
 *
 * Takes a `Date` rather than an ISO string because the value is the SERVER's
 * clock advanced locally, not a timestamp read off a record — and it is
 * rendered in Asia/Tashkent like every other time in this panel, so a staff
 * machine in another zone still reads the hour the deadline arithmetic uses.
 */
export function formatClock(at: Date | null): string {
  return at ? CLOCK.format(at) : '—';
}

/**
 * A metric that may not exist.
 *
 * §14's honesty stance in one function: the analytics endpoints OMIT a ratio or
 * an average that has no denominator, and an em dash is the panel's rendering
 * of "no data yet". Formatting `undefined` as `0` here would reintroduce the
 * exact false claim the response shape was designed to make impossible.
 */
export function formatMetric(value: number | undefined | null, suffix = ''): string {
  return value === undefined || value === null ? '—' : `${value}${suffix}`;
}

export function formatPercent(value: number | undefined | null): string {
  return formatMetric(value, '%');
}

export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const mib = bytes / (1024 * 1024);
  if (mib >= 1) return `${mib.toFixed(1)} MiB`;
  return `${(bytes / 1024).toFixed(0)} KiB`;
}

export function formatDuration(ms: number | undefined | null): string {
  if (ms === undefined || ms === null) return '—';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}:${String(seconds).padStart(2, '0')}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}
